import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Cookie, Envelope, Database, Eye, LockKey, Trash } from '@phosphor-icons/react';

export default function PrivacyPolicyPage() {
  const sections = [
    {
      icon: Cookie,
      color: '#FF8E00',
      title: '1. Cookies et traceurs',
      body: (
        <>
          <p>La page publique <strong>/</strong> ne dépose <strong>aucun cookie tiers</strong> ni traceur publicitaire.</p>
          <p>Une clé locale <code className="bg-[#F5F4EF] px-1.5 py-0.5 rounded text-xs">oscop_cookie_consent</code> est enregistrée dans le <em>localStorage</em> de votre navigateur pour mémoriser votre choix (accepter / refuser). Cette information ne quitte jamais votre appareil.</p>
        </>
      ),
    },
    {
      icon: Eye,
      color: '#00AEEF',
      title: '2. Statistiques anonymes',
      body: (
        <>
          <p>Si vous cliquez sur « J'accepte », nous mesurons :</p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-[#5E6D66]">
            <li>le nombre de visites de la page d'accueil (compteur agrégé par jour) ;</li>
            <li>les clics sur les CTA « Rejoindre la coopérative » et « Espace coopérateur » ;</li>
            <li>les clics de partage WhatsApp.</li>
          </ul>
          <p className="mt-2">Ces mesures sont <strong>totalement anonymes</strong> : nous ne collectons ni votre adresse IP, ni identifiant, ni profil de navigation. Le total est simplement incrémenté d'une unité côté serveur.</p>
        </>
      ),
    },
    {
      icon: Database,
      color: '#2D5A4C',
      title: '3. Données de conversion A/B (facultatif)',
      body: (
        <>
          <p>Si vous visitez la page pendant qu'une variante A/B est active, l'affichage de cette variante et le clic éventuel sur son CTA sont comptabilisés dans un tableau agrégé. Aucun identifiant utilisateur n'est stocké.</p>
        </>
      ),
    },
    {
      icon: LockKey,
      color: '#D5007D',
      title: '4. Espace coopérateur (compte)',
      body: (
        <>
          <p>Lorsque vous créez un compte sur <Link to="/register" className="text-[#2D5A4C] underline">/register</Link> ou vous connectez sur <Link to="/login" className="text-[#2D5A4C] underline">/login</Link>, nous stockons vos informations d'adhésion selon les termes du kit coopérateur (RGPD art. 6.1.b — exécution du contrat).</p>
          <p>Un cookie de session HTTP-only sécurisé (<code className="bg-[#F5F4EF] px-1.5 py-0.5 rounded text-xs">access_token</code>) est créé — il est indispensable au fonctionnement du CRM et ne peut être refusé.</p>
        </>
      ),
    },
    {
      icon: Trash,
      color: '#EA3C3A',
      title: '5. Retrait du consentement',
      body: (
        <>
          <p>Vous pouvez à tout moment :</p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-[#5E6D66]">
            <li>vider le localStorage de votre navigateur pour réafficher la bannière ;</li>
            <li>utiliser le mode Navigation privée pour ne rien stocker ;</li>
            <li>nous écrire pour demander la suppression de toute donnée liée à votre compte.</li>
          </ul>
        </>
      ),
    },
    {
      icon: Envelope,
      color: '#80C51F',
      title: '6. Contact',
      body: (
        <>
          <p>Responsable de traitement : <strong>Objectif SCOP Outremer</strong> — Coopérative des territoires ultramarins.</p>
          <p>Email : <a href="mailto:cmc@objectifscopoutremer.com" className="text-[#2D5A4C] underline">cmc@objectifscopoutremer.com</a></p>
          <p>Site : <a href="https://www.objectifscopoutremer.com" target="_blank" rel="noreferrer" className="text-[#2D5A4C] underline">www.objectifscopoutremer.com</a></p>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F5] text-[#1A2421]" data-testid="privacy-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {/* Header */}
      <header className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#5E6D66] hover:text-[#2D5A4C] font-semibold transition-colors"
            data-testid="privacy-back-home"
          >
            <ArrowLeft size={16} weight="bold" /> Retour à l'accueil
          </Link>
          <div className="flex gap-1">
            <span className="h-1 w-8 rounded-full" style={{ background: '#00AEEF' }} />
            <span className="h-1 w-6 rounded-full" style={{ background: '#80C51F' }} />
            <span className="h-1 w-4 rounded-full" style={{ background: '#EA3C3A' }} />
            <span className="h-1 w-3 rounded-full" style={{ background: '#FF8E00' }} />
            <span className="h-1 w-2 rounded-full" style={{ background: '#D5007D' }} />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-10 pb-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2D5A4C] to-[#1E4136] flex items-center justify-center text-white shadow-lg">
            <ShieldCheck size={22} weight="fill" />
          </div>
          <p className="text-xs uppercase tracking-[0.28em] text-[#2D5A4C] font-bold">Politique de confidentialité</p>
        </div>
        <h1 className="text-4xl md:text-5xl font-black leading-tight" style={{ fontFamily: 'Manrope' }}>
          Vos données, <span className="text-[#2D5A4C]">votre choix</span>.
        </h1>
        <p className="mt-3 text-base text-[#5E6D66] max-w-2xl">
          Objectif SCOP Outremer respecte votre vie privée. Cette page détaille exactement ce que nous mesurons sur la page publique <strong>/</strong>, ce que nous stockons quand vous créez un compte, et comment retirer votre consentement.
        </p>
        <p className="mt-2 text-xs text-[#8A968F]">Dernière mise à jour : 29 septembre 2026 — conforme RGPD (UE 2016/679).</p>
      </section>

      {/* Sections */}
      <section className="max-w-4xl mx-auto px-6 pb-16 space-y-4">
        {sections.map((s, i) => {
          const Icon = s.icon;
          return (
            <article
              key={i}
              className="bg-white rounded-2xl border border-[#E8E6E1] shadow-sm p-6 md:p-8"
              data-testid={`privacy-section-${i}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center text-white flex-shrink-0 shadow"
                  style={{ background: s.color }}
                >
                  <Icon size={22} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg md:text-xl font-extrabold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                    {s.title}
                  </h2>
                  <div className="mt-2 text-sm md:text-base text-[#5E6D66] space-y-2 leading-relaxed">
                    {s.body}
                  </div>
                </div>
              </div>
            </article>
          );
        })}

        {/* Footer CTA */}
        <div className="mt-6 rounded-2xl bg-[#1A2421] text-white px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-sm text-center md:text-left">
            Une question sur vos données ? Écrivez-nous à <a href="mailto:cmc@objectifscopoutremer.com" className="text-[#80C51F] font-bold underline">cmc@objectifscopoutremer.com</a>.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 bg-[#80C51F] hover:bg-[#6ea919] text-white text-sm font-bold px-4 py-2 rounded-full transition-colors"
            data-testid="privacy-footer-home"
          >
            <ArrowLeft size={14} weight="bold" /> Retour à l'accueil
          </Link>
        </div>
      </section>
    </div>
  );
}
